import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchJsonOrThrow } from '@/utils/fetch';
import { getPortfolioRefreshNonce } from '@/utils/portfolioRefresh';
import { retryNetworkOrServerError } from '@/utils/queryClient';
import { routes } from '@/utils/routes';
import type { PnLResponse, TokenPnL } from '@/utils/zerion/types';

const BATCH_SIZE = 100;
const MAX_TOKENS = 400;

interface TokenIdentifier {
  chainId: number;
  tokenAddress: string;
}

interface UseTokensPnLParams {
  evmAddress?: string;
  solanaAddress?: string;
  /** Token blocks whose members must be fetched in the same request batch */
  tokenBlocks: TokenIdentifier[][];
  enabled?: boolean;
  fetchAll?: boolean;
}

// Module-level so React Query can memoize the combined result: an inline
// combine would have a new identity every render, returning a new array each
// render and re-triggering render work indefinitely.
const combineLazyResults = (results: Array<UseQueryResult<TokenPnL[], Error>>) =>
  results.reduce<{ tokens: TokenPnL[]; error: Error | null; isFetching: boolean }>(
    (combined, result) => {
      if (result.data) combined.tokens.push(...result.data);
      combined.error ??= result.error;
      combined.isFetching ||= result.isFetching;
      return combined;
    },
    { tokens: [], error: null, isFetching: false },
  );

const fetchTokensPnL = (url: string) => fetchJsonOrThrow<PnLResponse>(url);

async function fetchTokensPnLBatch(
  evmAddress: string | undefined,
  solanaAddress: string | undefined,
  batch: TokenIdentifier[],
): Promise<TokenPnL[]> {
  const params = new URLSearchParams();
  if (evmAddress) params.set('evmAddress', evmAddress);
  if (solanaAddress) params.set('solanaAddress', solanaAddress);
  const refreshNonce = getPortfolioRefreshNonce();
  if (refreshNonce) params.set('_refresh', refreshNonce);
  params.set('tokens', batch.map((t) => `${t.chainId}:${t.tokenAddress}`).join(','));

  const result = await fetchTokensPnL(`${routes.api.WALLET_PNL}?${params.toString()}`);
  return result.tokens ?? [];
}

export const tokensPnLQueryKey = (params: {
  evmAddress?: string;
  solanaAddress?: string;
  tokens: readonly TokenIdentifier[];
}) => ['tokens-pnl', params] as const;

/**
 * Hook to fetch PnL data for tokens.
 * Fetches the first block-aligned batch eagerly and optionally fetches the remainder in parallel.
 * Returns a map of tokenId (chainId:tokenAddress) to per-token PnL data.
 */
export function useTokensPnL({
  evmAddress,
  solanaAddress,
  tokenBlocks,
  enabled = true,
  fetchAll = false,
}: UseTokensPnLParams) {
  const { eagerBatch, lazyBatches } = useMemo(() => {
    const batches: TokenIdentifier[][] = [];
    let currentBatch: TokenIdentifier[] = [];
    let tokenCount = 0;

    for (const block of tokenBlocks) {
      if (tokenCount + block.length > MAX_TOKENS) break;

      if (currentBatch.length > 0 && currentBatch.length + block.length > BATCH_SIZE) {
        batches.push(currentBatch);
        currentBatch = [];
      }

      currentBatch.push(...block);
      tokenCount += block.length;
    }

    if (currentBatch.length > 0) batches.push(currentBatch);

    return {
      eagerBatch: batches[0] ?? [],
      lazyBatches: batches.slice(1),
    };
  }, [tokenBlocks]);

  const { data: eagerTokensPnL, ...rest } = useQuery<TokenPnL[], Error>({
    queryKey: tokensPnLQueryKey({
      evmAddress,
      solanaAddress,
      tokens: eagerBatch,
    }),
    queryFn: () => fetchTokensPnLBatch(evmAddress, solanaAddress, eagerBatch),
    enabled: enabled && !!(evmAddress || solanaAddress) && eagerBatch.length > 0,
    staleTime: 30_000,
    retry: retryNetworkOrServerError,
    placeholderData: keepPreviousData,
  });

  const lazy = useQueries({
    queries: lazyBatches.map((batch) => ({
      queryKey: tokensPnLQueryKey({
        evmAddress,
        solanaAddress,
        tokens: batch,
      }),
      queryFn: () => fetchTokensPnLBatch(evmAddress, solanaAddress, batch),
      enabled: enabled && fetchAll && !!(evmAddress || solanaAddress),
      staleTime: 30_000,
      retry: retryNetworkOrServerError,
      placeholderData: keepPreviousData,
    })),
    combine: combineLazyResults,
  });

  const tokensPnLMap = useMemo(() => {
    const map = new Map<string, TokenPnL>();
    for (const result of [eagerTokensPnL, lazy.tokens]) {
      if (!result) continue;
      for (const tokenPnL of result) {
        map.set(tokenPnL.tokenId, tokenPnL);
      }
    }
    return map;
  }, [eagerTokensPnL, lazy.tokens]);

  return {
    /** Map of tokenId (chainId:tokenAddress) to per-token PnL data */
    tokensPnL: tokensPnLMap,
    ...rest,
    error: rest.error ?? lazy.error,
    isError: rest.isError || lazy.error !== null,
    isFetching: rest.isFetching || lazy.isFetching,
  };
}
